import { useState, useRef } from "react";
import SignaturePad from "react-signature-canvas";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { FileSignature, Smartphone, Send, Loader2, RotateCcw, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const CONSENT_TEXT = `This inspection will be documented with photographs, measurements, moisture readings, and AI-generated descriptions. All data collected will be used to generate a damage assessment report and estimate.`;

export default function Step3Consent({ workflow, request, requestId, onAdvance }) {
  const consent = workflow?.step_3 || {};
  const alreadySigned = !!consent.signed_at;
  const [mode, setMode] = useState(consent.method || null);
  const [signerName, setSignerName] = useState(consent.signer_name || request?.customer_name || "");
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  const clearSig = () => sigRef.current?.clear();

  const submitOnDevice = async () => {
    if (!signerName.trim()) return toast.error("Enter signer name");
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please sign in the box above");
    setSaving(true);
    try {
      const data_url = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      await API.post(`/inspection/${requestId}/step/3/sign`, {
        method: "on_device", signer_name: signerName.trim(), signature_data_url: data_url,
      });
      toast.success("Consent recorded");
      onAdvance(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save signature");
    } finally { setSaving(false); }
  };

  const submitSendARM = async () => {
    if (!signerName.trim()) return toast.error("Enter signer name");
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/3/sign`, {
        method: "send_arm", signer_name: signerName.trim(),
      });
      toast.success("Consent link sent to customer — they will sign on their ARM app.");
      onAdvance(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSaving(false); }
  };

  if (alreadySigned) {
    return (
      <div className="bg-white rounded-2xl border border-emerald-200 p-5 text-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
        <h2 className="text-base font-bold text-zinc-900">Step 3 · Consent Signed</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Signed by <strong>{consent.signer_name}</strong> at {new Date(consent.signed_at).toLocaleString()}
        </p>
        {consent.signature_url && (
          <img src={consent.signature_url} alt="Signature" className="h-20 mx-auto mt-3 bg-zinc-50 rounded border border-zinc-200" />
        )}
        <Button onClick={() => onAdvance(4)} className="mt-4 h-11 bg-zinc-900 text-white" data-testid="step-3-next">
          Continue to Step 4
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="step-3-consent">
      <div className="flex items-center gap-2">
        <FileSignature className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 3 · Inspection Consent</h2>
      </div>

      <Card><CardContent className="pt-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Consent & Recording Notice</p>
        <p className="text-xs text-zinc-700 leading-relaxed">{CONSENT_TEXT}</p>
      </CardContent></Card>

      {!mode && (
        <div className="space-y-2" data-testid="step-3-method-select">
          <Button onClick={() => setMode("on_device")}
                  className="w-full h-14 bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="step-3-on-device">
            <Smartphone className="w-5 h-5 mr-2" /> Sign on This Device
          </Button>
          <Button onClick={() => setMode("send_arm")} variant="outline"
                  className="w-full h-14"
                  data-testid="step-3-send-arm">
            <Send className="w-5 h-5 mr-2" /> Send to Customer's ARM App
          </Button>
        </div>
      )}

      {mode === "on_device" && (
        <Card><CardContent className="pt-4 space-y-3">
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Signer Name</Label>
            <Input value={signerName} onChange={e => setSignerName(e.target.value)}
                   className="h-11 mt-1" data-testid="step-3-signer-name" />
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Sign Below</Label>
            <div className="mt-1 border-2 border-dashed border-zinc-300 rounded-lg bg-white">
              <SignaturePad ref={sigRef}
                            canvasProps={{ className: "w-full h-40 rounded-lg", "data-testid": "step-3-sig-pad" }} />
            </div>
            <Button onClick={clearSig} variant="ghost" size="sm" className="mt-1 text-zinc-500" data-testid="step-3-sig-clear">
              <RotateCcw className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setMode(null)} variant="outline" className="h-11" data-testid="step-3-back">Back</Button>
            <Button onClick={submitOnDevice} disabled={saving}
                    className="h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
                    data-testid="step-3-submit-on-device">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Consent"}
            </Button>
          </div>
        </CardContent></Card>
      )}

      {mode === "send_arm" && (
        <Card><CardContent className="pt-4 space-y-3">
          <p className="text-xs text-zinc-600">
            We'll send a secure consent link via SMS to <strong>{request?.customer_phone || "the customer"}</strong>
            {request?.homeowner_fcm_token ? ", and a push notification to their ARM app." : "."}
          </p>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Signer Name (on record)</Label>
            <Input value={signerName} onChange={e => setSignerName(e.target.value)}
                   className="h-11 mt-1" data-testid="step-3-signer-name-arm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => setMode(null)} variant="outline" className="h-11" data-testid="step-3-back-arm">Back</Button>
            <Button onClick={submitSendARM} disabled={saving}
                    className="h-11 bg-zinc-900 hover:bg-zinc-800 text-white"
                    data-testid="step-3-submit-send-arm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Send Link</>}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-400 italic">
            Step will stay In Progress until the customer signs on their device.
          </p>
        </CardContent></Card>
      )}
    </div>
  );
}
